import {
  ChatDto,
  MessageDto,
  CreateChatBody,
  SendMessageBody,
  FeedbackMessageDto,
  FeedbackMessageBody,
  FeedbackFormBody,
  FeedbackFormDto,
  RequestContext,
  ChatListDto,
  ProvidersConf,
  GetChatParams,
} from './types';
import axios from 'axios';
import { getAuthApiHeaders, getBaseHeaders, getDomain, getPath } from './helper';
import { getRefChatQs } from 'utils/shared';

/**
 * Crea un chat
 * @param body
 * @param context
 */
const createChat = (body: CreateChatBody, context: RequestContext): Promise<ChatDto> => {
  return axios
    .post<ChatDto>(`${getDomain()}/${getPath(context.sandbox)}/chats`, JSON.stringify(body), {
      timeout: 90000,
      params: {
        ref: getRefChatQs(),
      },
      headers: {
        ...getAuthApiHeaders(),
        ...getBaseHeaders(context.sandbox, 'createChat'),
      },
    })
    .then(r => {
      return r.data;
    });
};

/**
 * Devuelve el chat con un id especifico
 * @param params
 * @param context
 * @param assistantId
 */
const getChat = (params: GetChatParams, context: RequestContext, assistantId: string): Promise<ChatDto> => {
  return axios
    .get<ChatDto>(`${getDomain()}/${getPath(context.sandbox)}/chats/${params.chatId}/${params.userId}/${assistantId}`, {
      params: {
        externalUserId: context.externalUserId,
      },
      timeout: 90000,
      headers: {
        ...getBaseHeaders(context.sandbox, 'getChat'),
        ...getAuthApiHeaders(),
      },
    })
    .then(r => r.data);
};

/**
 * Devuelve los mensages de un chat despues de un timestamp especifico
 * @param id
 * @param userId
 * @param context
 * @param assistantId
 * @param timestamp
 */
export const getChatMessagesFromTime = (id: string, userId: string, context: RequestContext, assistantId: string, timestamp: number): Promise<ChatDto> => {
  const partialPath = typeof context.temporalUserId === 'string' ? `${id}/${userId}` : `${id}${userId}`;
  return axios
    .get<ChatDto>(`${getDomain()}/${getPath(context.sandbox)}/chats/${partialPath}/${assistantId}`, {
      timeout: 90000,
      params: {
        fromTime: timestamp,
        externalUserId: context.externalUserId,
      },
      headers: {
        ...getBaseHeaders(context.sandbox, 'pollingcall'),
        ...getAuthApiHeaders(),
      },
    })
    .then(r => r.data);
};

/**
 * Obtiene la configuración del proveedor
 * @param context
 */
export const getProviderConfig = (context: RequestContext): Promise<ProvidersConf> => {
  return axios
    .get<ProvidersConf>(`${getDomain()}/chappie/assistants/configs`, {
      params: {
        id: context.assistantId,
        screen: context.mode,
        locale: context.locale,
      },
      timeout: 90000,
      headers: {
        ...getBaseHeaders(context.sandbox, 'getProviderConfig'),
      },
    })
    .then(r => r.data);
};

/**
 * Resuelve el chat, dependiendo si es creacion o carga
 * @param bodyOrParams
 * @param context
 * @param assistantId
 */
export const resolveChat = (bodyOrParams: CreateChatBody | GetChatParams, context: RequestContext, assistantId: string): Promise<ChatDto> => {
  const isGetChat = 'chatId' in bodyOrParams && 'userId' in bodyOrParams;

  return isGetChat ? getChat(bodyOrParams as GetChatParams, context, assistantId) : createChat(bodyOrParams as CreateChatBody, context);
};

/**
 * Envia un audio al server para obtener un texto
 * @param id
 * @param audioBlob
 * @param context
 */
export const sendAudioToServer = (id: string, audioBlob: Blob, context: RequestContext): Promise<string> => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'audio.wav');

  return axios
    .post<{ output: string }>(`${getDomain()}/whisperers/upload`, formData, {
      timeout: 90000,
      headers: {
        ...getBaseHeaders(context.sandbox, 'sendAudioToServer'),
        'Content-Type': 'multipart/form-data',
        'Chat-Id': id,
        'X-User-Id': context.temporalUserId,
      },
    })
    .then(r => r.data.output);
};

/**
 * Envia un mensaje
 * @param id
 * @param body
 * @param context
 */
export const sendMessage = (id: string, body: SendMessageBody, context: RequestContext): Promise<MessageDto> => {
  // Ensure text field has a default empty string value if undefined
  const bodyWithDefaults = {
    ...body,
    text: body.text ?? '',
  };

  return axios
    .put<MessageDto>(`${getDomain()}/${getPath(context.sandbox)}/chats/${id}`, JSON.stringify(bodyWithDefaults), {
      timeout: 90000,
      headers: {
        ...getAuthApiHeaders(),
        ...getBaseHeaders(context.sandbox, 'sendMessage'),
      },
    })
    .then(r => r.data)
    .then((message: MessageDto) => {
      // console.log('Message response: ', message);
      return message;
    });
};

/**
 * Envia feedback del mensaje
 * @param chatId
 * @param messageId
 * @param body
 * @param context
 */
export const sendFeedbackMessage = (chatId: string, messageId: string, body: FeedbackMessageBody, context: RequestContext): Promise<FeedbackMessageDto> => {
  return axios
    .post<FeedbackMessageDto>(`${getDomain()}/${getPath(context.sandbox)}/chats/${chatId}/messages/${messageId}/feedback`, JSON.stringify(body), {
      timeout: 20000,
      headers: {
        ...getBaseHeaders(context.sandbox, 'sendFeedbackMessage'),
      },
    })
    .then(r => r.data)
    .then((message: FeedbackMessageDto) => {
      // console.log('Message response: ', message);
      return message;
    });
};

/**
 * Envia feedback de la conversacion
 * @param chatId
 * @param body
 * @param context
 */
export const sendFeedback = (chatId: string, body: FeedbackFormBody, context: RequestContext): Promise<FeedbackFormDto> => {
  return axios
    .post<FeedbackFormDto>(`${getDomain()}/${getPath(context.sandbox)}/chats/${chatId}/feedback`, JSON.stringify(body), {
      timeout: 20000,
      headers: {
        ...getBaseHeaders(context.sandbox, 'sendFeedback'),
      },
    })
    .then(r => r.data)
    .then((message: FeedbackFormDto) => {
      // console.log('Message response: ', message);
      return message;
    });
};

/**
 * Retorna los chats de un usuario
 * @param chatId
 * @param userId
 * @param context
 */
export const getChatsFromUser = (context: RequestContext): Promise<ChatListDto[]> => {
  return axios
    .get<ChatListDto[]>(`${getDomain()}/${getPath(context.sandbox)}/assistants/${context.assistantId}/historics`, {
      timeout: 30000,
      params: {
        locale: context.locale,
      },
      headers: {
        ...getBaseHeaders(context.sandbox, 'getChatsFromUser'),
        ...getAuthApiHeaders(),
      },
    })
    .then(r => r.data)
    .then((response: any) => response.groups);
};

/**
 * Envia un evento de tracking
 * @param event
 * @param pageviewId
 * @param context
 */
export const sendTracking = (
  event: object,
  pageviewId: string,
  context: RequestContext,
): Promise<{
  correctness: string;
  isRejected: boolean;
  topicName: string;
}> => {
  const randomRequestId = `trackingcall-${Math.floor(10000 + Math.random() * 90000000)}`;
  return axios
    .put<{
      correctness: string;
      isRejected: boolean;
      topicName: string;
    }>(
      `${getDomain()}/${getPath(context.sandbox)}/assistants/tr/${context.assistantId}`,
      { ...event, pageview_id: pageviewId },
      {
        timeout: 35000,
        headers: {
          'X-Request-Id': randomRequestId,
        },
      },
    )
    .then(response => response.data);
};

/**
 * Asocia un chat a un usuario
 * Cuando un usuario deslogueado recupera un chat post login, hay que asociar el chat al usuario
 * @param chatId
 * @param context
 */
export const associateChatToUser = (chatId: string, context: RequestContext) => {
  return axios.patch<void>(`${getDomain()}/${getPath(context.sandbox)}/chats/${chatId}/users/${context.temporalUserId}/assistants/${context.assistantId}`, undefined, {
    timeout: 8000,
    headers: {
      ...getBaseHeaders(context.sandbox, 'associateChatCall'),
      ...getAuthApiHeaders(),
    },
  });
};
